import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useOutletContext } from "react-router-dom"
import StateData from 'components/reporting/components/StateData/StateData'
import Bloc from 'components/reporting/components/Bloc/Bloc'
import DoughnutChart from 'components/reporting/components/graphs/Doughnut/Doughnut'
import LineChart from 'components/reporting/components/graphs/Line/Line'
import { formatNb } from 'functions'
import { getDatasFrom } from 'helpers/chartjs'
import colors from 'components/reporting/styles/colors.scss'

const Production = () => {
  const { t } = useTranslation()
  const [contract] = useOutletContext()
  const [totalAdhesions, setTotalAdhesions] = useState(null)
  const [thisMonthAdhesions, setThisMonthAdhesions] = useState(null)
  const [totalCancellations, setTotalCancellations] = useState(null)
  const [productsSold, setProductsSold] = useState(null)
  const [soldPerPackage, setSoldPerPackage] = useState(null)
  const [adhesionPerPackage, setAdhesionPerPackage] = useState(null)
  const [topDistributors, setTopDistributors] = useState(null)
  const [primesPerPackage, setPrimesPerPackage] = useState(null)
  const [displayGraphs, setDisplayGraphs] = useState(false)
  const totalAdhesionPerPackage = formatNb(adhesionPerPackage?.data?.reduce( (acc, cur) => acc + cur ,0))
  const totalDistributors = formatNb(topDistributors?.data?.reduce( (acc, cur) => acc + cur ,0))


  useEffect(() => {
    if (!contract?._id) {
      setDisplayGraphs(false)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    // Reset datas
    setDisplayGraphs(true)
    setTotalAdhesions(null)
    setThisMonthAdhesions(null)
    setTotalCancellations(null)
    setProductsSold(null)
    setSoldPerPackage(null)
    setAdhesionPerPackage(null)
    setTopDistributors(null)
    setPrimesPerPackage(null)

    getDatasFrom({ contract, signal, endpoint: 'insuredProducts' }).then(setTotalAdhesions)
    getDatasFrom({ contract, signal, endpoint: 'thisMonthAdhesions' }).then(setThisMonthAdhesions)
    getDatasFrom({ contract, signal, endpoint: 'totalCancellations' }).then(setTotalCancellations)
    getDatasFrom({ contract, signal, endpoint: 'productsSold' }).then(setProductsSold)
    getDatasFrom({ contract, signal, endpoint: 'soldPerPackage' }).then(setSoldPerPackage)
    getDatasFrom({ contract, signal, endpoint: 'adhesionPerPackage' }).then(setAdhesionPerPackage)
    getDatasFrom({ contract, signal, endpoint: 'topDistributors' }).then(setTopDistributors)
    getDatasFrom({ contract, signal, endpoint: 'primesPerPackage' }).then(setPrimesPerPackage)

    return () => {
      controller.abort()
    }
  }, [contract])

  return displayGraphs 
  ? (
    <>
      <StateData
        title={t('reporting:graphs.totalAdhesions')}
        data={totalAdhesions}
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <StateData
        title={t('reporting:graphs.adhesionsThisMonth')}
        data={thisMonthAdhesions}
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <StateData
        title={t('reporting:graphs.totalCancellations')}
        data={totalCancellations}
        footerTitle={t('reporting:graphs.pastMonth')}
        hidePast={true}
      />

      <Bloc
        title={t('reporting:graphs.totalSold.title')}
        subtitle={t('reporting:graphs.range', {type: null})}
        size={contract?.partner && 'mid'}
        content={<LineChart
          backgroundColor={[
            colors.blue4
          ]}
          gradient={colors.blue4}
          legendID='productsSold'
          datas={productsSold}
          monthsAxisX={true}
        />}
      />

      <Bloc
        title={t('reporting:graphs.soldPerPackage.title')}
        subtitle={t('reporting:graphs.range', {type: null})}
        size={contract?.partner ? 'mid' : 'tierx2'}
        content={<LineChart
          backgroundColor={[
            colors.blue1,
            colors.blue4,
            colors.orange2,
            colors.orange4
          ]}
          legendID='soldPerPackage'
          datas={soldPerPackage}
          monthsAxisX={true}
        />}
      />

      <Bloc
        title={t('reporting:graphs.adhesionPerPackage.title')}
        subtitle={t('reporting:graphs.adhesionPerPackage.subtitle', {number: totalAdhesionPerPackage})}
        size={contract?.partner && !contract?.primesPerPackage ? 'mid' : 'tier'}
        content={<DoughnutChart
          backgroundColor={[
            colors.blue1,
            colors.blue2,
            colors.blue3,
            colors.blue4,
            colors.blue5
          ]}
          legendID='legendRateType'
          datas={adhesionPerPackage}
        />}
      />

      {contract?.partner && <Bloc
        title={t('reporting:graphs.topDistributor.title')}
        subtitle={t('reporting:graphs.topDistributor.subtitle', {number: totalDistributors})}
        size={contract?.primesPerPackage ? 'tier' : 'mid'}
        content={<DoughnutChart
          backgroundColor={[
            colors.orange1,
            colors.orange2,
            colors.orange3,
            colors.orange4,
            colors.orange5
          ]}
          legendID='legendTopDistributor'
          datas={topDistributors}
        />}
      />}

      {(contract?.partner && contract?.primesPerPackage) && <Bloc
        title={t('reporting:graphs.primesPerPackage.title')}
        subtitle={t('reporting:graphs.primesPerPackage.subtitle')}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.grey1,
            colors.grey2,
            colors.grey3,
            colors.grey4,
            colors.grey5
          ]}
          legendID='legendPrimesPerPackage'
          datas={primesPerPackage}
        />}
      />}
    </>
  )
  : t('reporting:noContract')
};

export default Production
